import React, { useState, useRef, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Zap, Plus, MoreVertical, Trash2, Lock } from 'lucide-react';
import { automationTriggerDisplayName } from '@frontend/app/containers/Projects/AutomationConfig/constants';
import { AutomationTriggerType } from '@frontend/app/types/globalTypes';
import { Button } from '@/shadcn/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';

interface TriggerNodeV2Props {
  data: FlowNodeData;
}

const getTriggerDescription = (triggerType?: string) => {
  // Use the standard V1 automation trigger display names for consistency
  if (triggerType && automationTriggerDisplayName[triggerType as AutomationTriggerType]) {
    return automationTriggerDisplayName[triggerType as AutomationTriggerType];
  }
  return 'This event starts the automation';
};

export const TriggerNodeV2: React.FC<TriggerNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    isEditing,
    metadata,
    isSelected,
    onNodeClick,
    onDeleteNode,
  } = data;
  const triggerType = metadata?.triggerType || 'PROGRAM_APPLIED';
  const [showOptionsPopover, setShowOptionsPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(false);
    onDeleteNode?.(nodeId);
  };

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(!showOptionsPopover);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowOptionsPopover(false);
      }
    };

    if (showOptionsPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOptionsPopover]);

  return (
    <div className="relative">
      {/* Main node container - Figma style */}
      <div className="flex flex-col items-start justify-center relative w-[342px] transition-all duration-200">
        {/* Node with border */}
        <div className={cn(
          'w-full bg-white border border-gray-200 rounded-2xl transition-all duration-200',
          isSelected && 'border-black border-2'
        )}
        >
          {/* Header Card */}
          <div
            className="w-full bg-white cursor-pointer rounded-t-2xl"
            onClick={handleNodeClick}
          >
            <div className="flex h-12 items-center gap-1 p-3 relative w-full">
              <div className="flex justify-between flex-1 self-stretch grow items-center relative">
                <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                  <div className="bg-lime-500 flex w-6 h-6 items-center justify-center relative rounded-lg">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <div className="relative w-fit font-medium text-gray-900 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                    When
                  </div>
                </div>
                <div className="inline-flex gap-2 flex-[0_0_auto] items-center relative">
                  <div ref={popoverRef}>
                    <Popover open={showOptionsPopover} onOpenChange={setShowOptionsPopover}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex flex-col w-6 h-6 items-center justify-center gap-2 px-2 py-0.5 bg-white rounded-lg hover:bg-gray-100"
                          onClick={handleOptionsClick}
                        >
                          <MoreVertical className="w-3 h-3 text-gray-600" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-1" align="end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={handleDelete}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete Node
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Separator line */}
          <div className="w-full h-px bg-gray-200" />

          {/* Content Card */}
          <div className="w-full bg-white rounded-b-2xl overflow-hidden">
            <div className="flex flex-col items-start gap-3 px-3 py-3 relative w-full bg-white">
              <div className="flex items-center justify-between px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-gray-100 rounded-xl overflow-hidden">
                <div className="text-sm font-medium text-gray-700">
                  {getTriggerDescription(triggerType)}
                </div>
                <div className="gap-2 p-1.5 flex w-6 h-6 items-center justify-center relative rounded-lg">
                  <Lock className="w-3 h-3 text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom connector with add button - positioned like DelayNodeV2 */}
        {isEditing && (
          <div className="relative w-full flex justify-center -mt-3 z-10">
            <Button
              size="sm"
              className="flex w-6 h-6 items-center justify-center p-0 bg-gray-600 rounded-full border-2 border-white hover:bg-gray-700 shadow-md"
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                data.onShowOptionBar?.(nodeId, {
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
              }}
            >
              <Plus className="w-3 h-3 text-white" />
            </Button>
          </div>
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />
    </div>
  );
};
